import { Suspense, lazy, useEffect } from "react";

import { Loader2 } from "lucide-react";

import { Toaster } from "@/components/ui/toaster";

import { Toaster as Sonner } from "@/components/ui/sonner";

import { TooltipProvider } from "@/components/ui/tooltip";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { BrowserRouter, Routes, Route } from "react-router-dom";

import { AuthProvider } from "@/contexts/AuthContext";

import { ProtectedRoute } from "@/components/ProtectedRoute";

import { MemberLayout } from "@/components/layouts/MemberLayout";

import { AdminLayout } from "@/components/layouts/AdminLayout";

import { ThemeProvider } from "@/components/theme-provider";

import ErrorBoundary from "@/components/ErrorBoundary";

import { ResourceHints } from "@/components/ResourceHints";

import { preloadRoutes } from "@/utils/routePreloader";



// Lazy load pages

const Landing = lazy(() => import("./pages/Landing"));

const Auth = lazy(() => import("./pages/Auth"));

const EmailVerification = lazy(() => import("./pages/EmailVerification"));

const SelectPlan = lazy(() => import("./pages/SelectPlan"));

const Checkout = lazy(() => import("./pages/Checkout"));

const CheckoutComplete = lazy(() => import("./pages/CheckoutComplete"));

const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));

const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));

const MemberDashboard = lazy(() => import("./pages/dashboard/MemberDashboard"));

const ClientsPage = lazy(() => import("./pages/dashboard/ClientsPage"));

const ClientDetailsPage = lazy(() => import("./pages/dashboard/ClientDetailsPage"));

const ProjectsPage = lazy(() => import("./pages/dashboard/ProjectsPage"));

const ContractsPage = lazy(() => import("./pages/dashboard/ContractsPage"));

const NotificationsPage = lazy(() => import("./pages/dashboard/NotificationsPage"));

const ProfilePage = lazy(() => import("./pages/dashboard/ProfilePage"));

const SubscriptionPage = lazy(() => import("./pages/dashboard/SubscriptionPage"));

const DominiosPage = lazy(() => import("./pages/dashboard/DominiosPage"));

const HospedagensPage = lazy(() => import("./pages/dashboard/HospedagensPage"));

const SitesPage = lazy(() => import("./pages/dashboard/SitesPage"));

const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));

const AdminPlansPage = lazy(() => import("./pages/admin/AdminPlansPage"));

const AdminPaymentsPage = lazy(() => import("./pages/admin/AdminPaymentsPage"));

const AdminSubscriptionsPage = lazy(() => import("./pages/admin/AdminSubscriptionsPage"));

const AdminApiDocsPage = lazy(() => import("./pages/admin/AdminApiDocsPage"));

const UsageLimitsPage = lazy(() => import("./pages/dashboard/UsageLimitsPage"));

const NotFound = lazy(() => import("./pages/NotFound"));



const queryClient = new QueryClient();



const LoadingFallback = () => (

  <div className="flex items-center justify-center min-h-screen">

    <Loader2 className="h-8 w-8 animate-spin text-primary" />

  </div>

);



const AppContent = () => {

  useEffect(() => {

    // Preload critical routes after initial render

    preloadRoutes([

      { name: 'dashboard', loader: () => import("./pages/dashboard/MemberDashboard") },

      { name: 'clients', loader: () => import("./pages/dashboard/ClientsPage") },

      { name: 'select-plan', loader: () => import("./pages/SelectPlan") },

    ]);

  }, []);



  return (

    <>

      <ResourceHints />

      <Routes>

        {/* Public routes */}

        <Route path="/" element={<Landing />} />

        <Route path="/auth" element={<Auth />} />

        <Route path="/auth/login" element={<Auth />} />

        <Route path="/auth/signup" element={<Auth />} />

        <Route path="/email-verification" element={<EmailVerification />} />



        {/* Protected routes - requires auth */}

        <Route path="/select-plan" element={

          <ProtectedRoute>

            <SelectPlan />

          </ProtectedRoute>

        } />

        <Route path="/payment/success" element={

          <ProtectedRoute>

            <PaymentSuccess />

          </ProtectedRoute>

        } />

        <Route path="/checkout" element={

          <ProtectedRoute>

            <Checkout />

          </ProtectedRoute>

        } />

        <Route path="/checkout/complete" element={

          <ProtectedRoute>

            <CheckoutComplete />

          </ProtectedRoute>

        } />

        <Route path="/payment/return" element={

          <ProtectedRoute>

            <PaymentSuccess />

          </ProtectedRoute>

        } />

        <Route path="/payment/cancel" element={

          <ProtectedRoute>

            <PaymentCancel />

          </ProtectedRoute>

        } />



        {/* Member dashboard - requires subscription */}

        <Route path="/dashboard" element={

          <ProtectedRoute requireSubscription>

            <MemberLayout />

          </ProtectedRoute>

        }>

          <Route index element={<MemberDashboard />} />

          <Route path="clientes" element={<ClientsPage />} />

          <Route path="clientes/:id" element={<ClientDetailsPage />} />

          <Route path="projects" element={<ProjectsPage />} />

          <Route path="contracts" element={<ContractsPage />} />

          <Route path="notifications" element={<NotificationsPage />} />

          <Route path="profile" element={<ProfilePage />} />

          <Route path="subscription" element={<SubscriptionPage />} />

          <Route path="dominios" element={<DominiosPage />} />

          <Route path="hospedagens" element={<HospedagensPage />} />

          <Route path="sites" element={<SitesPage />} />

          <Route path="usage" element={<UsageLimitsPage />} />

        </Route>



        {/* Admin dashboard - requires admin role */}

        <Route path="/admin" element={

          <ProtectedRoute requireAdmin>

            <AdminLayout />

          </ProtectedRoute>

        }>

          <Route index element={<AdminDashboard />} />

          <Route path="users" element={<AdminUsersPage />} />

          <Route path="plans" element={<AdminPlansPage />} />

          <Route path="payments" element={<AdminPaymentsPage />} />

          <Route path="subscriptions" element={<AdminSubscriptionsPage />} />

          <Route path="api-docs" element={<AdminApiDocsPage />} />

          <Route path="profile" element={<ProfilePage />} />

        </Route>



        <Route path="*" element={<NotFound />} />

      </Routes>

    </>

  );

};



const App = () => (

  <ErrorBoundary>

    <QueryClientProvider client={queryClient}>

      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">

        <TooltipProvider delayDuration={0}>

          <Toaster />

          <Sonner />

          <BrowserRouter>

            <AuthProvider>

              <Suspense fallback={<LoadingFallback />}>

                <AppContent />

              </Suspense>

            </AuthProvider>

          </BrowserRouter>

        </TooltipProvider>

      </ThemeProvider>

    </QueryClientProvider>

  </ErrorBoundary>

);



export default App;



